import { useCallback, useRef } from 'react';

import { smoothScrollTo } from './smoothScrollTo';

/**
 * Returns a stable callback that, on each call, scrolls the next matching
 * element into view (cycling back to the first after the last).
 *
 * The set of matching elements is re-queried on every call, so additions and
 * removals between calls are handled automatically. If the previously scrolled
 * element is no longer present, the first match is used as the starting point.
 *
 * @param selector - A valid CSS selector string used to find candidate elements.
 * @param offsetPx - Top offset in pixels passed to `smoothScrollTo` (default: 48).
 */
export const useScrollToNextElement = (selector: string, offsetPx?: number) => {
  const lastScrolledElementRef = useRef<HTMLElement | null>(null);

  return useCallback(() => {
    const elements = Array.from(
      globalThis.document.querySelectorAll<HTMLElement>(selector),
    );

    if (elements.length === 0) {
      return;
    }

    const lastElement = lastScrolledElementRef.current;
    let targetIndex = 0;

    if (lastElement) {
      const lastIndex = elements.indexOf(lastElement);

      if (lastIndex !== -1) {
        targetIndex = (lastIndex + 1) % elements.length;
      }
    }

    const targetElement = elements[targetIndex];

    lastScrolledElementRef.current = targetElement;
    smoothScrollTo(targetElement, offsetPx);
  }, [selector, offsetPx]);
};
